class MoqStepper extends HTMLElement {
    static get observedAttributes() { return ['moq', 'value']; }

    connectedCallback() {
        this.moq = Math.max(1, parseInt(this.getAttribute('moq') || '1', 10));
        this.value = this.snap(parseInt(this.getAttribute('value') || this.moq, 10));
        this.name = this.getAttribute('name') || 'quantity';
        this.forwardedAttrs = this.captureForwardedAttrs();
        this.render();
    }

    captureForwardedAttrs() {
        const skip = new Set(['moq', 'value', 'name', 'class', 'style']);
        const attrs = {};
        Array.from(this.attributes).forEach((attr) => {
            if (!skip.has(attr.name)) attrs[attr.name] = attr.value;
        });
        return attrs;
    }

    snap(n) {
        if (isNaN(n) || n < this.moq) return this.moq;
        return Math.round(n / this.moq) * this.moq || this.moq;
    }

    render() {
        const isReadonly = this.hasAttribute('readonly');

        this.innerHTML = `
          <div class="moq-stepper__wrap${isReadonly ? ' moq-stepper__wrap--readonly' : ''}">
            ${!isReadonly ? `
              <button type="button" class="moq-stepper__btn" data-dir="-1" aria-label="${window.quantityStrings?.decrease || 'Decrease quantity'}">
                ${this.iconMinus()}
              </button>
            ` : ''}
            <div class="moq-stepper__mid">
              <input
                class="moq-stepper__input quantity__input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                name="${this.name}"
                value="${this.value}"
                aria-label="${window.quantityStrings?.label || 'Quantity'}"
                autocomplete="off"
              >
              <span class="moq-stepper__badge">${(window.quantityStrings?.multiplesOf || 'multiples of')} ${this.moq}</span>
            </div>
            ${!isReadonly ? `
              <button type="button" class="moq-stepper__btn" data-dir="1" aria-label="${window.quantityStrings?.increase || 'Increase quantity'}">
                ${this.iconPlus()}
              </button>
            ` : ''}
          </div>
        `;

        this.input = this.querySelector('.moq-stepper__input');
        this.minusBtn = this.querySelector('[data-dir="-1"]');

        Object.entries(this.forwardedAttrs).forEach(([attrName, val]) => {
            this.input.setAttribute(attrName, val);
        });
        this.removeAttribute('id');

        this.updateDisabled();
        this.bind();
    }

    iconMinus() {
        return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/></svg>`;
    }
    iconPlus() {
        return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>`;
    }

    updateDisabled() {
        this.minusBtn.disabled = this.value <= this.moq;
    }

    bump() {
        this.input.classList.remove('is-snapping');
        void this.input.offsetWidth;
        this.input.classList.add('is-snapping');
    }

    setValue(v, { emit = true, animate = true } = {}) {
        const snapped = this.snap(v);
        const changed = snapped !== this.value;
        this.value = snapped;
        this.input.value = this.value;
        this.updateDisabled();
        if (animate && changed) this.bump();
        if (emit) {
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
            this.dispatchEvent(new CustomEvent('moq:change', {
                detail: { value: this.value, moq: this.moq },
                bubbles: true,
            }));
        }
    }

    bind() {
        this.querySelectorAll('.moq-stepper__btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const dir = parseInt(btn.dataset.dir, 10);
                this.setValue(this.value + dir * this.moq);
            });
        });
        this.input.addEventListener('blur', () => {
            this.setValue(parseInt(this.input.value, 10) || this.moq);
        });
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.input.blur();
            if (e.key === 'ArrowUp') { e.preventDefault(); this.setValue(this.value + this.moq); }
            if (e.key === 'ArrowDown') { e.preventDefault(); this.setValue(this.value - this.moq); }
        });
    }
}
customElements.define('moq-stepper', MoqStepper);
